import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiGet } from "../api/client";

interface Regulation {
  id: string;
  title: string;
  kind: string;
  jurisdiction: string;
  issued_at: string | null;
  effective_at: string | null;
  status: string;
  source_url: string | null;
  meta: Record<string, string>;
}

interface Version {
  id: string;
  version_no: number;
  issued_at: string | null;
  effective_at: string | null;
  status: string;
  checksum: string;
  summary: string;
  source_url: string | null;
}

export default function RegulationDetail() {
  const { id } = useParams();
  const [reg, setReg] = useState<Regulation | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [rels, setRels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      apiGet<Regulation>(`/regulations/${id}`),
      apiGet<Version[]>(`/regulations/${id}/versions`),
      apiGet<any[]>(`/regulations/${id}/relations`),
    ]).then(([r, v, rel]) => {
      setReg(r);
      setVersions(v);
      setRels(rel);
      setLoading(false);
    });
  }, [id]);

  if (loading || !reg) {
    return (
      <div className="mt-4 flex animate-pulse gap-4">
        <div className="flex-1 space-y-3">
          <div className="h-8 w-3/4 rounded bg-border" />
          <div className="h-4 w-1/2 rounded bg-border" />
          <div className="h-4 w-1/3 rounded bg-border" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/" className="text-sm text-muted hover:text-text mb-3 inline-block">
        ← Kembali
      </Link>

      <button
        onClick={() => {}}
        className="mb-4 inline-flex items-center gap-2 rounded border border-border px-3 py-1.5 text-sm hover:bg-primary-500 hover:text-white"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Tracing path
      </button>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-lg border border-border p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-2.5 py-0.5 text-xs font-semibold text-white">
                {reg.kind}
              </span>
              <span className="rounded-full bg-surface border border-border px-2.5 py-0.5 text-xs font-medium text-muted">
                {reg.jurisdiction}
              </span>
              <span className="rounded-full bg-aspace px-2.5 py-0.5 text-xs font-semibold text-aspace">
                {reg.status}
              </span>
            </div>
            <h1 className="mt-3 text-2xl font-bold text-text">{reg.title}</h1>
            <p className="mt-1 text-sm text-muted">
              {reg.issued_at} · berlaku sejak {reg.effective_at || "—"}
            </p>
            {reg.meta.scope && (
              <p className="mt-2 text-sm text-muted">
                <span className="font-medium text-text">Scope:</span> {reg.meta.scope}
              </p>
            )}
            {reg.source_url && (
              <a
                href={reg.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-xs text-primary underline"
              >
                {reg.source_url}
              </a>
            )}
          </div>

          <h2 className="text-xl font-bold text-text">Riwayat Versi</h2>
          {versions.length === 0 ? (
            <p className="text-sm text-muted">Belum ada versi.</p>
          ) : (
            <div className="space-y-3">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className={`flex flex-wrap items-center justify-between gap-3 rounded border border-border p-3 ${
                    v.status === "aktif"
                      ? "bg-surface"
                      : "opacity-60"
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-xs text-muted">v{v.version_no}</span>
                    <span className="text-xs text-muted">{v.issued_at || "—"}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        v.status === "aktif"
                          ? "bg-success text-white"
                          : "bg-border text-muted"
                      }`}
                    >
                      {v.status}
                    </span>
                  </div>
                  <span className="font-mono text-[11px] text-muted">
                    {v.checksum}
                  </span>
                </div>
              ))}
            </div>
          )}

          <h2 className="text-xl font-bold text-text">Hubungan (Tracing)</h2>
          {rels.length === 0 ? (
            <p className="text-sm text-muted">Tidak ada relasi tracing.</p>
          ) : (
            <div className="space-y-2">
              {rels.map((rel: any) => (
                <div
                  key={rel.id}
                  className="flex flex-wrap items-center gap-2 rounded border border-border p-3"
                >
                  <code>{rel.parent_id}</code>
                  <span className="text-xs text-muted">→</span>
                  <code>{rel.child_id}</code>
                  <span className="rounded-full bg-border px-2 py-0.5 text-[11px] font-semibold text-muted">
                    {rel.relation}
                  </span>
                  {rel.notes && <span className="text-xs text-muted">({rel.notes})</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-border p-4">
            <h3 className="text-sm font-semibold text-text">Metadata</h3>
            <dl className="mt-2 space-y-1 text-sm">
              <dt className="text-muted">ID</dt>
              <dd className="font-mono text-xs text-text">{reg.id}</dd>
              <dt className="text-muted">Status</dt>
              <dd className="text-sm text-text">{reg.status}</dd>
              <dt className="text-muted">Dari</dt>
              <dd className="text-sm text-text">{reg.issued_at || "—"}</dd>
              <dt className="text-muted">Ke</dt>
              <dd className="text-sm text-text">{reg.effective_at || "—"}</dd>
            </dl>
          </div>

          <div className="rounded-lg border border-border p-4">
            <h3 className="text-sm font-semibold text-text">Checksum Ringkasan</h3>
            <pre className="mt-2 font-mono text-xs text-muted overflow-x-auto">
              {reg.meta?.checksum || "—"}
            </pre>
          </div>

          <div className="rounded-lg border border-border p-4">
            <h3 className="text-sm font-semibold text-text">Akun QC</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-green-500" />
                <span className="text-xs text-muted">Hukum</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-green-500" />
                <span className="text-xs text-muted">IT</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-green-500" />
                <span className="text-xs text-muted">Visual</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
